import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Clock, RotateCcw } from "lucide-react";
import { formatDateTimeForDisplay } from "@/lib/datetime-utils";
interface CurrentTimeSelectorProps {
  currentTime: Date;
  onTimeChange: (newTime: Date) => void;
}
const CurrentTimeSelector = ({
  currentTime,
  onTimeChange
}: CurrentTimeSelectorProps) => {
  // Form state for date and time inputs
  const [dateInput, setDateInput] = useState(currentTime.toISOString().split("T")[0]);
  const [timeInput, setTimeInput] = useState(`${String(currentTime.getHours()).padStart(2, "0")}:${String(currentTime.getMinutes()).padStart(2, "0")}`);

  // Handler to apply the selected date and time
  const handleApply = () => {
    try {
      const [year, month, day] = dateInput.split("-").map(Number);
      const [hours, minutes] = timeInput.split(":").map(Number);
      const newDate = new Date();
      newDate.setFullYear(year, month - 1, day);
      newDate.setHours(hours, minutes, 0, 0);
      onTimeChange(newDate);
    } catch (error) {
      console.error("Invalid date or time format", error);
    }
  };

  // Reset to actual current time
  const handleReset = () => {
    const now = new Date();
    onTimeChange(now);
    setDateInput(now.toISOString().split("T")[0]);
    setTimeInput(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);
  };

  // Presets for quick testing
  const setTo2PM = () => {
    const today = new Date();
    today.setHours(14, 0, 0, 0);
    onTimeChange(today);
    setDateInput(today.toISOString().split("T")[0]);
    setTimeInput("14:00");
  };
  const setTo3PM = () => {
    const today = new Date();
    today.setHours(15, 0, 0, 0);
    onTimeChange(today);
    setDateInput(today.toISOString().split("T")[0]);
    setTimeInput("15:00");
  };
  return <Card className="p-4 border-green-200 bg-transparent">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-sm flex items-center">
          <Clock className="w-4 h-4 mr-1" /> Test Mode: Current Time
        </h3>
        <Button variant="ghost" size="sm" onClick={handleReset} className="h-8 px-2 text-xs">
          <RotateCcw className="w-3 h-3 mr-1" /> Reset
        </Button>
      </div>
      
      <div className="text-xs text-muted-foreground mb-2">
        Current test time: {formatDateTimeForDisplay(currentTime)}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <div className="flex gap-2">
            <div className="w-1/2">
              <Label htmlFor="date-input" className="text-xs">Date</Label>
              <Input id="date-input" type="date" value={dateInput} onChange={e => setDateInput(e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="w-1/2">
              <Label htmlFor="time-input" className="text-xs">Time</Label>
              <Input id="time-input" type="time" value={timeInput} onChange={e => setTimeInput(e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-end">
          <Button onClick={handleApply} size="sm" className="h-8">Apply</Button>
        </div>
      </div>

      <div className="flex gap-2 mt-2">
        <Button variant="outline" size="sm" onClick={setTo2PM} className="text-xs h-7">
          Set to 2:00 PM
        </Button>
        <Button variant="outline" size="sm" onClick={setTo3PM} className="text-xs h-7">
          Set to 3:00 PM
        </Button>
      </div>
    </Card>;
};
export default CurrentTimeSelector;